import { get, cloneDeep } from "lodash";
import { createSelector } from "reselect";
import operationSelector from "../operations/operationSelector.js";
import userSelector from "../users/userSelector.js";

// -- HELPERS -- //

const typeFromProps = props => {
  const { type } = props.match.params;
  return type;
};

const entityIdFromProps = props => {
  const { entityid, entitytype } = props.match.params;

  if (!entityid || !entitytype) {
    throw `missing property - id:${entityid} type:${entitytype}`;
  }

  return `/${entitytype}/${entityid}`;
};

// do we also need to be able to build full project objects here?
// or should we just be using raw objects in all cases
const buildFullResource = (resource, state) => {
  if (!resource) return null;

  const id = resource["@id"];

  // create duplicate of selected resource obj
  // stringify duplication method will work as all data is in JSON-LD format
  let result = JSON.parse(JSON.stringify(resource));

  // join with other loaded entities
  result.region = get(
    state,
    `regions.dataMap.${resource.region}`,
    resource.region
  );
  result.zone = get(state, `zones.dataMap.${resource.zone}`, resource.zone);
  result.image = get(state, `images.dataMap.${resource.image}`, resource.image);
  result.size = get(state, `sizes.dataMap.${resource.size}`, resource.size);

  // optional values
  // omission from the original entity is a permissions decision so don't alter that
  const usage = get(state, `usage.dataMap.${id}`, null);
  if (usage) result.usage = usage;

  const measurements = get(state, `measurements.dataMap.${id}`, null);
  if (measurements) result.measurements = measurements;

  return result;
};

// -- QUERIES -- //

const genericResourceQuery = (state, props) => {
  const id = entitySelector.entityIdFromProps(props);
  const type = entitySelector.typeFromProps(props);
  const entity = get(state, `ephemeralEntities.dataMap.${id}`, null);

  return entitySelector.buildFullResource(entity, state);
};

const genericIsFetchingQuery = (state, props) => {
  const type = entitySelector.typeFromProps(props);
  return get(state, `${type}.isFetching`, null);
};

const genericIsErroredQuery = (state, props) => {
  const type = entitySelector.typeFromProps(props);
  return get(state, `${type}.isErrored`, null);
};

// wrapper selector so it can be exported as part of the resourceSelector object
const selectEntity = () => {
  return createSelector(
    [entitySelector.genericResourceQuery, operationSelector.permissionsQuery],
    (entity, permissions) => {
      if (!entity) return null;

      const entityClone = cloneDeep(entity);

      return operationSelector.stampOperationPermissions(
        entityClone,
        permissions
      );
    }
  );
};

const selectEntityUserIsEntityOwner = () => {
  return createSelector(
    [entitySelector.genericResourceQuery, userSelector.selectUser],
    (entity, user) => {
      if (!entity || !user) return false;
      return entity.ownedBy === user["@id"];
    }
  );
};

const selectIsFetching = () => {
  return createSelector(
    [entitySelector.genericIsFetchingQuery],
    isFetching => isFetching
  );
};

const selectIsErrored = () => {
  return createSelector(
    [entitySelector.genericIsErroredQuery],
    isErrored => isErrored
  );
};

// using the following export pattern so we can mock more easily using jest

const entitySelector = {
  typeFromProps,
  entityIdFromProps,
  buildFullResource,
  genericResourceQuery,
  genericIsFetchingQuery,
  genericIsErroredQuery,
  selectEntity,
  selectEntityUserIsEntityOwner,
  selectIsFetching,
  selectIsErrored
};

export default entitySelector;
